import { useState, ChangeEvent } from "react";
import { toast } from "sonner";
import { useStytchB2BClient } from "@stytch/react/b2b";
import posthog from "../../lib/posthog";
import PalommaLogo from "../../components/PalommaLogo";
import EmailSentConfirmation from "../../components/auth/EmailSentConfirmation";
import MarketingContent from "../../components/auth/MarketingContent";
import AuthForm from "../../components/auth/AuthForm";

type SignUpFormData = {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  merchantType: "rentals" | "development";
  acceptedTerms: boolean;
};

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const SignUpContainer = (): JSX.Element => {
  const stytch = useStytchB2BClient();

  const [mode, setMode] = useState<"signup" | "login">(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("signup") === "true" ? "signup" : "login";
  });
  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    merchantType: "rentals",
    acceptedTerms: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleInputChange = (field: keyof SignUpFormData) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      merchantType: e.target.value as "rentals" | "development",
    }));
  };

  const handleTermsChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      acceptedTerms: checked,
    }));
  };

  const handleModeToggle = () => {
    setMode(mode === "signup" ? "login" : "signup");
  };

  const handleBackFromEmailSent = () => {
    setEmailSent(false);
    setMode("signup");
    setFormData({
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      merchantType: "rentals",
      acceptedTerms: false,
    });
  };

  const isFormValid = (): boolean => {
    if (mode === "login") {
      return validateEmail(formData.email);
    }
    return (
      formData.firstName.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.companyName.trim() !== "" &&
      validateEmail(formData.email) &&
      formData.acceptedTerms
    );
  };

  const handleSubmit = async (): Promise<void> => {
    if (!isFormValid()) return;

    setIsSubmitting(true);
    try {
      // Store signup data only for signup mode
      if (mode === "signup") {
        localStorage.setItem('pendingSignup', JSON.stringify({
          leadName: `${formData.firstName} ${formData.lastName}`.trim(),
          companyName: formData.companyName,
          email: formData.email,
          merchantType: formData.merchantType,
          timestamp: new Date().toISOString(),
        }));
        // Store auth mode to determine redirect after authentication
        localStorage.setItem('authMode', 'signup');
      } else {
        // Clear any pending signup data for login mode
        localStorage.removeItem('pendingSignup');
        // Store auth mode to determine redirect after authentication
        localStorage.setItem('authMode', 'login');
      }

      // Send discovery email magic link
      await stytch.magicLinks.email.discovery.send({
        email_address: formData.email,
        discovery_redirect_url: `${window.location.origin}/auth/discovery`,
        locale: "es",
      });

      setEmailSent(true);

      posthog.capture("magic link sent", {
        mode,
        email: formData.email,
        ...(mode === "signup" && {
          company_name: formData.companyName,
          merchant_type: formData.merchantType,
        }),
      });

      toast.success("Correo enviado", {
        description: "Por favor revisa tu correo electrónico para verificar tu cuenta.",
      });
    } catch (error) {
      console.error("Error during signup:", error);
      posthog.captureException(error);
      toast.error("Error", {
        description: "Hubo un problema al enviar el correo. Por favor intenta de nuevo.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#F4F3F8]">
      {/* Logo in top left */}
      <div className="absolute left-8 top-8 hidden md:block">
        <PalommaLogo height={50} width={50} />
      </div>

      {/* Main content container - centered */}
      {emailSent ? (
        <div className="flex w-full max-w-[600px] px-4 md:px-8 py-4 md:py-8">
          <EmailSentConfirmation
            email={formData.email}
            onBack={handleBackFromEmailSent}
          />
        </div>
      ) : (
        <div className="flex w-full max-w-[1400px] flex-col items-stretch gap-6 px-4 py-4 md:flex-row md:gap-12 md:px-8 md:py-8">
          {/* Left side - Marketing content */}
          <div className="hidden flex-1 md:flex">
            <MarketingContent />
          </div>

          {/* Right side - Form card */}
          <div className="flex w-full shrink-0 flex-col md:w-[550px]">
            <AuthForm
              mode={mode}
              formData={formData}
              isSubmitting={isSubmitting}
              isFormValid={isFormValid()}
              onInputChange={handleInputChange}
              onSelectChange={handleSelectChange}
              onTermsChange={handleTermsChange}
              onSubmit={handleSubmit}
              onModeToggle={handleModeToggle}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUpContainer;
